import React, { useMemo, useState, useRef, useLayoutEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { CHART_POSITION, getChartScales, getTooltipContent } from '../costBenchmarkHelpers.js';
import '../CostBenchmark.scss';
import Chart from './Chart.js';
import Legend from './Legend.js';
import { CostBenchmarkContext } from '../../../contexts/costBenchmarkingContext.js';
import { ForgeTooltip } from '@tylertech/forge-react';
import { useNameConventions } from '../../../contexts/nameConventionsContext.js';

const ScatterChartD3 = ({ data = [], uniqueId = 'hide' }) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 700 });
  const { activeTooltip } = useContext(CostBenchmarkContext);
  const { nameConventions } = useNameConventions();
  

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width } = entry.contentRect;
      const dynamicHeight = Math.max(700, data.length * 10);
      setDimensions({ width, height: dynamicHeight });
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [data.length]);

  const innerWidth = Math.max(0, dimensions.width - CHART_POSITION.left - CHART_POSITION.right);
  const innerHeight = Math.max(0, dimensions.height - CHART_POSITION.top - CHART_POSITION.bottom);

  const chartData = useMemo(() => {
    if (innerWidth <= 0 || innerHeight <= 0 || !data.length) return null;

    const { xScale, yScale } = getChartScales(data, innerWidth, innerHeight);
    return {
      xScale,
      yScale,
      xTicks: xScale.ticks(6),
      yTicks: yScale.ticks(8),
    };
  }, [data, innerWidth, innerHeight]);

  if (!chartData) return <div ref={containerRef} className="fullWidth" />;

  const { xScale, yScale, xTicks, yTicks } = chartData;
  return (
    <div id="cost-benchmark-scatter-chart" ref={containerRef} className="chart-wrapper flexContainer withGap direction-column fullWidth alignVertically">
      <Chart xScale={xScale} yScale={yScale} xTicks={xTicks} yTicks={yTicks} data={data} dimensions={dimensions} innerHeight={innerHeight} innerWidth={innerWidth} uniqueId={uniqueId} />
      <Legend />
      {activeTooltip.visible && activeTooltip.targetId && (
        <ForgeTooltip
          className="chart-tooltip"
          anchor={activeTooltip.targetId || ''}
          open={activeTooltip.visible}
        >
          {activeTooltip.data ? getTooltipContent(activeTooltip.data, nameConventions) : ''}
        </ForgeTooltip>
      )}
    </div>
  );
};

ScatterChartD3.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  uniqueId: PropTypes.string
};

export default ScatterChartD3;
